import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Observable, TimeoutError } from 'rxjs';
import { tap, timeout } from 'rxjs/operators';
import { Logger, LoggerFactory } from '@rillion/core/logger';
import { AppSettingsService } from '@rillion/core/services';
import { HttpError } from '../http-error';
import { NetworkService } from '@rillion/core/http/network.service';
import { ErrorHandlerService } from './error-handler.service';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly TIMEOUT_MS = 30000;
  private readonly logger: Logger;
  private offline = false;

  constructor(
    private route: Router,
    private translateService: TranslateService,
    private errorHandler: ErrorHandlerService,
    private networkService: NetworkService,
    private appSettingsService: AppSettingsService,
    loggerFactory: LoggerFactory,
  ) {
    this.logger = loggerFactory.getLogger('HttpErrorInterceptor');
    this.logger.info('HttpErrorInterceptor is started');
    this.networkService
      .isOffline()
      .pipe(tap((offline: boolean) => (this.offline = offline)))
      .subscribe();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      timeout(this.TIMEOUT_MS),
      tap({
        error: (response: HttpErrorResponse) => {
          let message: string;
          let errorCode = '';

          switch (response.status) {
            case -1:
            case 0:
              this.logger.debug('Offline:', this.offline);
              message = this.noNetworkMessage();
              break;
            case 307: // Temporary redirect (may provides a new URL for the browser)
            case 400: // Bad Request
            case 401:
            case 403: // Forbidden
            case 412: // Precondition Failed
            case 415: // Unsupported media type
            case 422: // Unprocessable entity
              // Display the message from the server
              message = this.extractErrorMessage(response);
              if (response.error) {
                errorCode = response.error.code;
              }
              // Auto logout on invalid token (401 Unauthorized) from API
              if (this.route.url.toLowerCase() !== '/login' && !this.route.url.includes('/verify-email') && response.status === 401) {
                this.errorHandler.handle401(this.appSettingsService.appSettings.loginRedirectUrl);
                this.logger.warn('Received 401 Unauthorized. Logging out and redirect to /login');
              }
              break;
            case 404: // Not found
            case 503: // Service temporarily unavailable
            case 504: // Gateway timeout
              message = this.serverNotReachableMessage();
              break;
            default:
              message = this.genericServerErrorMessage();
              break;
          }
          if (response instanceof TimeoutError) {
            message = response.message;
          }
          const httpError: HttpError = new HttpError(response.status, message, errorCode);
          this.logger.error(httpError.message);
        },
      }),
    );
  }

  /* Helpers for creating proper messages displayed in ui */

  private genericServerErrorMessage(): string {
    return this.translateService.instant('GENERIC_SERVER_ERROR');
  }

  private noNetworkMessage(): string {
    return this.translateService.instant('NO_NETWORK');
  }

  private serverNotReachableMessage() {
    return this.translateService.instant('SERVER_NOT_REACHABLE');
  }

  public extractErrorMessage(response: HttpErrorResponse): string {
    const error = response?.error?.error;

    if (error?.details?.[0].message) {
      return error.details[0].message;
    } else if (error?.message) {
      return error.message;
    }

    return this.genericServerErrorMessage();
  }
}
